/**
 * Thin client for the panel's own origin.
 *
 * `/api/*` is proxied server-side onto the backend's `/api/admin/*` with the
 * API and admin secrets attached there, so nothing in this file ever holds a
 * credential — the session cookie is the browser's only token.
 */

import { translate } from './i18n';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The session expired or was never established. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

/** A query bag: primitive values only; anything else is dropped. */
type Query = Record<string, unknown>;

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; query?: Query } = {},
): Promise<T> {
  const url = options.query ? `${path}?${toQueryString(options.query)}` : path;

  const response = await fetch(url, {
    method,
    headers: {
      Accept: 'application/json',
      ...(options.body !== undefined
        ? { 'Content-Type': 'application/json' }
        : {}),
      // The proxy rejects unsafe methods without this; a cross-site form post
      // cannot set it, which is what makes it a CSRF check.
      'X-Requested-With': 'fetch',
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    credentials: 'same-origin',
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, extractMessage(payload, response));
  }
  return payload as T;
}

function toQueryString(query: Query): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // Omit empty values entirely: the backend's validator rejects unknown or
    // blank fields rather than treating them as "no filter".
    if (value === undefined || value === null || value === '') continue;
    // Objects and arrays have no meaningful query representation here; the
    // API takes flat scalars only.
    if (
      typeof value !== 'string' &&
      typeof value !== 'number' &&
      typeof value !== 'boolean'
    ) {
      continue;
    }
    params.set(key, String(value));
  }
  return params.toString();
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractMessage(payload: unknown, response: Response): string {
  if (typeof payload === 'string' && payload) return payload;
  if (payload && typeof payload === 'object') {
    const message = (payload as { message?: unknown }).message;
    // Nest's ValidationPipe returns an array of field errors.
    if (Array.isArray(message)) return message.join('; ');
    if (typeof message === 'string') return message;
  }
  return translate('common.requestFailed', { status: response.status });
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>('PATCH', path, { body }),
  /**
   * DELETE with a body — unusual, and deliberate. Every destructive route on
   * this API requires a written reason, and the reason belongs in the request
   * rather than in a query string where it would end up in an access log.
   */
  delete: <T>(path: string, body?: unknown) =>
    request<T>('DELETE', path, { body }),
};

/**
 * Drops `undefined` keys so an untouched filter control contributes nothing to
 * the request instead of sending an empty string the API would reject.
 */
export function compact<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, v]) => v !== undefined && v !== null && v !== '',
    ),
  ) as Partial<T>;
}
